import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { ClockSmallIcon, Body2, ArrowRefreshIcon, Tooltip, Checkbox, IconButton } from "@activecollab/components";
import classnames from "classnames";
import moment from "moment";
import PropTypes from "prop-types";
import useAcSelectDate from "~/components/Hooks/useAcSelectDate.jsx";
import { MessageAttachment } from "./MessageAttachment.jsx";
import { MessageOptions } from "./MessageOptions.jsx";
import {
  editConversationMessage,
  deleteConversationMessage,
  markConversationMessageAsUnread,
} from "../../../../../store/actions/actions.conversationMessages.jsx";
import { makeGetAllAttachmentsByParent } from "../../../../../store/selectors/selector.attachments.jsx";
import { makeParentHasReaction } from "../../../../../store/selectors/selector.reactions.jsx";
import { getUserById } from "../../../../../store/selectors/selector.users.jsx";
import { formatLocalTime, formatLocalDate } from "../../../../../utils/date.jsx";
import { useFilePreviewContext } from "../../../../FilePreview/context/index.jsx";
import { StartReacting, LeavedReactions } from "../../../../Reactions/index.jsx";
import AvatarContainer from "../../../../Users/AvatarContainer.jsx";
import { useConversationContentContext } from "../../../context/ConversationContentContext.jsx";
import { CREATE_TASK_MODE } from "../index.jsx";

export const Message = ({
  id,
  children,
  left,
  pending,
  createdOn,
  formatTime,
  formatDate,
  onDelete,
  onClose,
  onEdit,
  changedOn,
  error,
  onResend,
  body,
  showAvatar,
  type,
  createdById,
  attachments,
  conversationId,
  createdByName,
  createdByEmail,
}) => {
  const {
    manualSetNewMessageSince,
    mode,
    onChangeSelectedMessage,
    mapOfCheckedMessages = {},
  } = useConversationContentContext();
  const { t } = useTranslation();
  const { today } = useAcSelectDate();

  const { openFilePreview } = useFilePreviewContext();
  const dispatch = useDispatch();
  const inSelectMode = useMemo(() => {
    return [CREATE_TASK_MODE].some((value) => value === mode);
  }, [mode]);

  const getParentHasReaction = useMemo(makeParentHasReaction, []);
  const parentHasReaction = useSelector((state) => {
    return getParentHasReaction(state, "UserMessage", id);
  });

  const getAllAttachments = useMemo(() => {
    return makeGetAllAttachmentsByParent({
      parentType: "ActiveCollab\\Module\\System\\Model\\Message\\UserMessage",
      parentId: id,
    });
  }, [id]);
  const allAttachments = useSelector(getAllAttachments);

  const [openMenu, setOpenMenu] = useState(false);
  const isChecked = mapOfCheckedMessages[id] ?? false;

  const user = useSelector((state) => getUserById(state, createdById));
  const userName = user?.get("display_name");

  const formatDateTime = useCallback(
    (value) => {
      const yesterday = today.clone().subtract(1, "day");

      if (today.format("YYYY-MM-DD") === moment.unix(value).format("YYYY-MM-DD")) {
        return `${t("Today at")} ${formatLocalTime(value, formatTime)}`;
      }
      if (yesterday.format("YYYY-MM-DD") === moment.unix(value).format("YYYY-MM-DD")) {
        return `${t("Yesterday at")} ${formatLocalTime(value, formatTime)}`;
      }
      return `${formatLocalDate(value, formatDate, false)} ${t("at")} ${formatLocalTime(value, formatTime)} `;
    },
    [formatDate, formatTime, t, today]
  );

  const createdOnFormat = useMemo(() => {
    return formatDateTime(createdOn);
  }, [createdOn, formatDateTime]);

  const changedOnFormat = useMemo(() => {
    return `${t("Edited")} ${formatDateTime(changedOn)}`;
  }, [changedOn, formatDateTime, t]);

  const handleResendMessage = useCallback(() => {
    typeof onResend === "function" && onResend({ body }, id);
  }, [onResend, body, id]);

  const handleOpenPreview = useCallback(
    (id) => {
      openFilePreview(id, allAttachments);
    },
    [allAttachments, openFilePreview]
  );

  const handleRemoveAttachment = useCallback(
    (attachmentId) => {
      attachments.length === 1 && !body
        ? dispatch(deleteConversationMessage(conversationId, id))
        : dispatch(
            editConversationMessage(conversationId, id, {
              body,
              drop_attached_files: [attachmentId],
            })
          );
    },
    [dispatch, conversationId, body, id, attachments]
  );

  const handleOpenMenu = useCallback(() => {
    setOpenMenu(true);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(false);
    typeof onClose === "function" && onClose();
  }, [onClose]);

  const handleCloseReaction = useCallback(() => {
    typeof onClose === "function" && onClose();
  }, [onClose]);

  const handleMarkAsUnread = useCallback(() => {
    manualSetNewMessageSince(createdOn - 1);
    dispatch(markConversationMessageAsUnread(conversationId, id));
  }, [conversationId, createdOn, dispatch, id, manualSetNewMessageSince]);

  const handleSelectMessage = useCallback(
    (event) => {
      const checked = event.target.checked;
      if (typeof onChangeSelectedMessage === "function") {
        onChangeSelectedMessage(id, checked);
      }
    },
    [id, onChangeSelectedMessage]
  );

  return (
    <div
      className={classnames("conversation-message-wrapper tw-grow tw-px", {
        "tw-mb-3": showAvatar,
        "show-opts": openMenu,
      })}
    >
      <div
        className={classnames("tw-flex tw-items-center conversation-message-body", {
          "conversation-message-body-bg": isChecked && inSelectMode,
        })}
      >
        {!left || inSelectMode ? (
          <div
            className={classnames("tw-flex flex-shrink-0 tw-justify-center tw-w-10", {
              "tw-items-center": inSelectMode,
              "tw-self-end": !inSelectMode,
            })}
          >
            {inSelectMode ? (
              <Checkbox
                id={`message-checkbox-${id}`}
                className="tw-flex-none"
                onChange={handleSelectMessage}
                checked={isChecked}
              />
            ) : null}
            {!inSelectMode && !left && showAvatar ? (
              <Tooltip
                title={userName || createdByName}
                disable={
                  type === "OneToOneConversation" && user && !user?.get(`is_trashed`) && !user?.get(`is_archived`)
                }
              >
                <AvatarContainer
                  id={createdById}
                  size={24}
                  style={{ marginBottom: "2px" }}
                  name={createdByName}
                  email={createdByEmail}
                />
              </Tooltip>
            ) : null}
          </div>
        ) : null}
        <div
          className={classnames("tw-flex tw-items-center tw-flex-1", {
            "tw-justify-end": left,
            "tw-justify-start": !left,
          })}
        >
          <div
            className={classnames("tw-flex tw-items-center", {
              "tw-mr-2 tw-justify-end tw-flex-row-reverse": left,
              "tw-justify-start": !left,
            })}
          >
            <div
              className={classnames("conversation-message", {
                "logged-user tw-px-2 tw-py-1": left,
                "interlocutor tw-px-2 tw-py-1": !left,
                "tw-opacity-50": left && error,
              })}
              title={createdOnFormat}
            >
              {children ? (
                <Body2
                  color={left ? "primary" : "page-paper"}
                  lineHeight="loose"
                  className={classnames("message-text-body tw-px-1")}
                  wordBreak="smart"
                >
                  <span dangerouslySetInnerHTML={{ __html: children }} />
                  {changedOn && !pending && !error ? (
                    <Tooltip title={changedOnFormat}>
                      <span className="tw-opacity-50">&nbsp;({t("Edited")})</span>
                    </Tooltip>
                  ) : null}
                </Body2>
              ) : null}
              {attachments?.length > 0 ? (
                <div className="message-attachments-wrapper tw-my-1">
                  {attachments.map((id) => (
                    <MessageAttachment
                      id={id}
                      key={`message-attachment-${id}`}
                      size={500}
                      onClick={handleOpenPreview}
                      onRemove={handleRemoveAttachment}
                      isFromLoggedUser={left}
                    />
                  ))}
                </div>
              ) : null}
            </div>
            {!pending && !error && !inSelectMode ? (
              <>
                <StartReacting
                  onClose={handleCloseReaction}
                  parentType="UserMessage"
                  parentId={id}
                  className={classnames({ "tw-mr-1": left, "tw-ml-1": !left })}
                />
                <MessageOptions
                  id={id}
                  onDelete={left ? onDelete : undefined}
                  onClose={handleCloseMenu}
                  onOpen={handleOpenMenu}
                  onEdit={left ? onEdit : undefined}
                  onMarkAsUnread={!left ? handleMarkAsUnread : undefined}
                />
              </>
            ) : null}
            {pending ? <ClockSmallIcon className="tw-mr-1" /> : null}
            {left && error && !pending ? (
              <Tooltip title={t("Resend")}>
                <IconButton
                  variant="text gray"
                  className="tw-mr-1"
                  onClick={handleResendMessage}
                >
                  <ArrowRefreshIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </div>
        </div>
      </div>
      {!pending && parentHasReaction ? (
        <div className="tw-flex tw-items-center conversation-message-reactions-wrapper">
          <div className="tw-flex flex-shrink-0 tw-w-10" />
          <LeavedReactions
            parentType="UserMessage"
            parentId={id}
            disableToggle={inSelectMode}
            className={classnames("messages_reactions tw-flex tw-items-center tw-flex-1 tw-mr-2", {
              "tw-justify-end": left,
              "tw-justify-start": !left,
            })}
          />
        </div>
      ) : null}
    </div>
  );
};

Message.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  createdById: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  createdOn: PropTypes.number.isRequired,
  changedOn: PropTypes.number,
  children: PropTypes.node,
  left: PropTypes.bool,
  pending: PropTypes.bool,
  error: PropTypes.bool,
  onDelete: PropTypes.func,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  onResend: PropTypes.func,
  body: PropTypes.string,
  showAvatar: PropTypes.bool,
  type: PropTypes.string,
  attachments: PropTypes.array,
  conversationId: PropTypes.number,
  createdByName: PropTypes.string,
  createdByEmail: PropTypes.string,
  formatTime: PropTypes.string,
  formatDate: PropTypes.string,
};

Message.defaultProps = {
  createdById: 0,
  left: false,
  pending: false,
  error: false,
  body: undefined,
  showAvatar: false,
  attachments: [],
};
